import PageHeading from "../components/PageHeading";
import SiteGuide from "../components/SiteGuide";
import React, { useState, useEffect, useRef } from "react";
import { User, Shield, Save, Eye, EyeOff, Lock, Mail, Phone, Camera, BookOpen } from "lucide-react";
import axios from "../axios/AxiosInstance";
import { message } from "antd";

export default function MonComptePage() {
  // State management
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [mail, setMail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");

  const photoInput = useRef(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  // Photo conservée uniquement dans ce navigateur, par compte utilisateur.
  // À connecter à une API de stockage du profil pour la synchroniser entre appareils.
  const photoKey = `jm-profile-photo:${localStorage.getItem("userId") || localStorage.getItem("userEmail") || "session"}`;
  const [photo, setPhoto] = useState(() => {
    try { return localStorage.getItem(photoKey) || ""; } catch { return ""; }
  });
  const selectPhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 5 * 1024 * 1024) {
      message.error("Choisissez une photo JPG, PNG ou WebP de 5 Mo maximum.");
      return;
    }
    setPhotoBusy(true);
    const url = URL.createObjectURL(file);
    try {
      const image = new Image();
      await new Promise((resolve, reject) => { image.onload = resolve; image.onerror = reject; image.src = url; });
      const canvas = document.createElement("canvas");
      const ratio = Math.min(1, 512 / Math.max(image.width, image.height));
      canvas.width = Math.max(1, Math.round(image.width * ratio));
      canvas.height = Math.max(1, Math.round(image.height * ratio));
      canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);
      const result = canvas.toDataURL("image/webp", 0.85);
      localStorage.setItem(photoKey, result);
      setPhoto(result);
      message.success("Photo enregistrée.");
    } catch {
      message.error("Impossible d’enregistrer cette photo. Essayez un autre fichier.");
    } finally {
      URL.revokeObjectURL(url);
      setPhotoBusy(false);
    }
  };
  const removePhoto = () => {
    try { localStorage.removeItem(photoKey); setPhoto(""); message.success("Photo supprimée."); }
    catch { message.error("Impossible de supprimer la photo."); }
  };

  const updateProfile = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      // La route backend est PATCH /user (auth/update-profile n'existe pas)
      const payload = {
        firstname: firstName,
        lastname: lastName,
      };
      if (mail) payload.email = mail;
      if (phone) payload.phone = phone;

      await axios.patch("user", payload);

      // Synchroniser le localStorage pour que le reste de l'app reste cohérent
      localStorage.setItem("firstname", firstName || "");
      localStorage.setItem("lastname", lastName || "");
      if (mail) localStorage.setItem("userEmail", mail);
      if (phone) localStorage.setItem("phone", phone);

      message.success("Profil mis à jour avec succès !");
    } catch (error) {
      console.error("Erreur mise à jour profil:", error);
      message.error("Erreur lors de la mise à jour du profil");
    } finally {
      setIsLoading(false);
    }
  };

  const updatePassword = async () => {
    if (!password || !newPassword || !confirmPassword) return;
    if (newPassword !== confirmPassword) {
      message.error("Les mots de passe ne correspondent pas");
      return;
    }

    if (isLoading) return;
    setIsLoading(true);
    try {
      await axios.post("auth/update-password", { password, newPassword });
      message.success("Mot de passe modifié avec succès !");
      setPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      message.error("Erreur lors de la modification du mot de passe");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const firstName = localStorage.getItem("firstname");
    setFirstName(firstName || "");
    const lastName = localStorage.getItem("lastname");
    setLastName(lastName || "");
    const mail = localStorage.getItem("userEmail");
    setMail(mail || "");
    const phone = localStorage.getItem("phone");
    setPhone(phone || "");
  }, []);

  const fieldClass = "mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100";
  const name = [firstName, lastName].filter(Boolean).join(" ") || "Votre profil";
  const initials = `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase() || "JM";
  const submitClass = "inline-flex items-center justify-center gap-2 rounded-xl bg-blue-700 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50";

  return <div>
    <div className="mb-6"><PageHeading icon="account" title="Mon compte" description="Vos coordonnées, la sécurité de votre accès et le guide du site JM Intérim." /></div>
    <div className="grid items-start gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
      <aside className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="mb-4 flex h-24 w-24 items-center justify-center overflow-hidden rounded-2xl bg-blue-100 text-2xl font-bold text-blue-900">{photo ? <img src={photo} alt="Votre photo de profil" className="h-full w-full object-cover" /> : initials}</div>
        <input ref={photoInput} type="file" accept="image/jpeg,image/png,image/webp" aria-label="Choisir une photo de profil" className="sr-only" onChange={selectPhoto} disabled={photoBusy} />
        <div className="mb-2 flex flex-wrap items-center gap-3">
          <button type="button" disabled={photoBusy} onClick={() => photoInput.current?.click()} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-blue-900 hover:bg-blue-50 disabled:opacity-50"><Camera size={15} />{photoBusy ? "Enregistrement…" : photo ? "Changer la photo" : "Ajouter une photo"}</button>
          {photo && <button type="button" disabled={photoBusy} onClick={removePhoto} className="text-xs text-slate-500 hover:text-red-600">Supprimer</button>}
        </div>
        <p className="mb-5 text-xs leading-relaxed text-slate-500">JPG, PNG ou WebP · 5 Mo maximum.</p>
        <p className="break-words text-lg font-semibold text-slate-900">{name}</p>
        <p className="mt-1 break-words text-sm text-slate-500">{mail || "Renseignez vos coordonnées"}</p>
        <nav aria-label="Rubriques du compte" className="mt-6 space-y-2 border-t border-slate-100 pt-4">
          {[{id: "profile", label: "Informations personnelles", icon: User}, {id: "security", label: "Mot de passe", icon: Shield}, {id: "guide", label: "Guide du site", icon: BookOpen}].map(({id, label, icon: Icon}) => <button key={id} type="button" aria-current={activeTab === id ? "page" : undefined} onClick={() => setActiveTab(id)} className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium ${activeTab === id ? "bg-blue-50 text-blue-900" : "text-slate-600 hover:bg-slate-50"}`}><Icon size={18} className="shrink-0" />{label}</button>)}
        </nav>
      </aside>
      <section className="min-w-0 rounded-2xl border border-slate-200 bg-white">
        <header className="border-b border-slate-100 px-6 py-5">
          <h2 className="text-xl font-semibold text-slate-900">{activeTab === "guide" ? "Guide du site" : activeTab === "profile" ? "Informations personnelles" : "Modifier mon mot de passe"}</h2>
          <p className="mt-1 text-sm text-slate-500">{activeTab === "guide" ? "Comprenez chaque rubrique et accédez rapidement aux fonctionnalités dont vous avez besoin." : activeTab === "profile" ? "Gardez vos coordonnées à jour pour faciliter les échanges avec l’agence." : "Confirmez votre mot de passe actuel pour enregistrer le nouveau."}</p>
        </header>
        {activeTab === "guide" ? <SiteGuide /> : activeTab === "profile" ? <form className="p-6" onSubmit={e => { e.preventDefault(); updateProfile(); }}>
          <div className="grid gap-5 sm:grid-cols-2">
            {[
              ["firstName", "Prénom", firstName, setFirstName, "text", "given-name", User],
              ["lastName", "Nom", lastName, setLastName, "text", "family-name", User],
              ["email", "Adresse email", mail, setMail, "email", "email", Mail],
              ["phone", "Téléphone", phone, setPhone, "tel", "tel", Phone],
            ].map(([id, label, value, setter, type, autoComplete, Icon]) => <label key={id} htmlFor={id} className="text-sm font-medium text-slate-700"><span className="flex items-center gap-2"><Icon size={16} className="text-slate-400" />{label}</span><input id={id} type={type} autoComplete={autoComplete} required={id !== "phone"} value={value} onChange={e => setter(e.target.value)} className={fieldClass} /></label>)}
          </div>
          <footer className="mt-7 flex flex-wrap items-center justify-between gap-4 border-t border-slate-100 pt-5"><p className="text-xs text-slate-500">Les changements sont appliqués après enregistrement.</p><button type="submit" className={submitClass} disabled={isLoading}><Save size={16} />{isLoading ? "Enregistrement…" : "Enregistrer les modifications"}</button></footer>
        </form> : <form className="p-6" onSubmit={e => { e.preventDefault(); updatePassword(); }}>
          <div className="max-w-xl space-y-5">
            {[
              ["currentPassword", "Mot de passe actuel", password, setPassword, showPassword, setShowPassword, "current-password"],
              ["newPassword", "Nouveau mot de passe", newPassword, setNewPassword, showNewPassword, setShowNewPassword, "new-password"],
              ["confirmPassword", "Confirmer le nouveau mot de passe", confirmPassword, setConfirmPassword, showConfirmPassword, setShowConfirmPassword, "new-password"],
            ].map(([id, label, value, setter, visible, toggle, autoComplete]) => <div key={id}><label htmlFor={id} className="text-sm font-medium text-slate-700">{label}</label><div className="relative"><input id={id} required type={visible ? "text" : "password"} autoComplete={autoComplete} value={value} onChange={e => setter(e.target.value)} className={`${fieldClass} pr-12`} /><button type="button" aria-label={`${visible ? "Masquer" : "Afficher"} : ${label}`} aria-pressed={visible} onClick={() => toggle(!visible)} className="absolute bottom-3 right-3 rounded text-slate-500 hover:text-blue-800">{visible ? <EyeOff size={20} /> : <Eye size={20} />}</button></div></div>)}
            {confirmPassword && newPassword !== confirmPassword && <p role="alert" className="text-sm text-red-600">Les mots de passe ne correspondent pas.</p>}
            <p className="rounded-xl bg-slate-50 p-3 text-xs leading-relaxed text-slate-500">Choisissez un mot de passe unique et évitez d’utiliser votre nom ou votre date de naissance.</p>
          </div>
          <footer className="mt-7 flex justify-end border-t border-slate-100 pt-5"><button type="submit" className={submitClass} disabled={isLoading || !password || !newPassword || newPassword !== confirmPassword}><Lock size={16} />{isLoading ? "Enregistrement…" : "Enregistrer le nouveau mot de passe"}</button></footer>
        </form>}
      </section>
    </div>
  </div>;
}
